import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { Dataset, DatasetFilters, datasetsConstants } from "../../api/Datasets";
import { MultiEditTable } from "../../common/tables/MultiEdit/MultiEditTable";
import {
  GenericVirtualizedTableCells,
  GenericVirtualizedTableFunctionRef,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { switchDatasetsDefaultSortState } from "./DatasetsTable";
import { useHistory, useLocation } from "react-router-dom";
import { Alert } from "../../common/overlays/Alert/Alert";
import {
  MultiEditMappedItemObject,
  MultiEditProvider,
} from "../../common/forms/MultiEditForms/common/MultiEditProvider";
import { MultiEditInputForm } from "../../common/forms/MultiEditForms/forms/MultiEditInputForm";
import { MultiEditSelectForm } from "../../common/forms/MultiEditForms/forms/MultiEditSelectForm";
import { MultiEditHeaderWrapper } from "../../common/forms/MultiEditForms/common/MultiEditFormWrapper";
import {
  generateBulkEditYupSchema,
  getValueOrDefault,
  useMultiEditUtils,
} from "../../common/forms/MultiEditForms/common/MultiEditUtils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SessionContext } from "../../common/contexts/SessionContext";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { GenericBooleanStateProvider } from "../../common/modals/Modal/GenericModal";
import { RenderDatasetPreview } from "../ManualUpload/components/RenderDatasetPreview";
import { BulkEditLocationState } from "../../common/tables/MultiEdit/BulkEditDropdown/BulkEditDropDownTypes";
import { ProjectsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import { projectsConstants } from "../../api/Projects";
import { Button } from "../../common/buttons/Button/Button";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { customTypeConstants } from "../../api/CustomTypes/CustomTypes";
import { CustomTypesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/CustomTypesVirtualizedSelectForm";
import { useMultiEditCustomFieldTableCells } from "../../Customization/CustomTypes/generics/useCustomTypesEntityTable";
import { useCustomTypes } from "../../Customization/CustomTypes/generics/useCustomTypes";
import {
  processInitialValuesForTypedEntity,
  processValuesForTypedEntity,
  useCustomFieldsValidationObjectShape,
} from "../../Customization/CustomTypes/generics/CustomTypeValidationUtils";
import { InferSortByOptions } from "../../api/GenericTypes";
import { getSortingFn } from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { ColumnWidthSettings } from "../../common/tables/Tabs/TableTabsTypes";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";

export const DatasetsBulkEditTable = () => {
  const history = useHistory();
  const { session } = useContext(SessionContext);
  const location = useLocation<BulkEditLocationState<Dataset, DatasetFilters>>();
  const defaultFilters = useMemo(() => (location && location.state?.filters) || {}, [location]);
  const initItems = useMemo(() => (location && location.state?.items) || undefined, [location]);
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const functionRef = useRef<GenericVirtualizedTableFunctionRef<Dataset>>(null);
  const [filters, setFilters] = useState<DatasetFilters>({ ...defaultFilters });
  const [sort, setSort] = useState<SortState<InferSortByOptions<DatasetFilters>>[]>(
    switchDatasetsDefaultSortState(filters.sortBy)
  );
  const [typeOverride, setTypeOverride] = useState(false);
  const { types } = useCustomTypes({ entityType: "Dataset", filters: { isEnabled: true } });

  const isRequired = useRef(false);

  // Hook to update searchTerm or orderBy if filter changes; (e.g. triggered by the sidebar)
  useMemo(() => {
    if (defaultFilters.sortBy !== undefined) {
      setSort(switchDatasetsDefaultSortState(defaultFilters.sortBy));
    }
  }, [defaultFilters.sortBy]);

  const { control, getValues } = useForm<Dataset>({ defaultValues: {} });

  const fixedRowRendererColumnWidth = 35;
  const fixedRowRendererColumn = useCallback(() => {
    return <></>;
  }, []);

  const fixedRowRenderer = useCallback((row: Dataset) => {
    return (
      <GenericBooleanStateProvider>
        {({ isTrue: showModal, setIsTrue: setShowModal }) => (
          <>
            <button title="Preview" className="btn btn-xs btn-round btn-primary" onClick={() => setShowModal(true)}>
              <LucideIcon name="zoom-in" />
            </button>
            <RenderDatasetPreview
              datasetId={row.id}
              name={row.name}
              showModal={showModal}
              setShowModal={setShowModal}
            />
          </>
        )}
      </GenericBooleanStateProvider>
    );
  }, []);

  const cfColumns = useMultiEditCustomFieldTableCells({
    types: types,
    control: control,
    getValues: getValues,
    selection: selection,
    disabled: false,
    sort,
    setSort,
    setFilters,
  });

  const columns: GenericVirtualizedTableCells<Dataset> = useMemo(() => {
    let res: GenericVirtualizedTableCells<Dataset> = [
      {
        id: "default-id",
        label: "ID",
        Header: "ID",
        accessor: (row) => (
          <div
            className={"flex row-nowrap align-center"}
            style={{ padding: "7px", background: "var(--gray-100)", width: "100%", cursor: "not-allowed" }}
          >
            <span style={{ color: "var(--gray-400)" }}>
              <samp>{row.id}</samp>
            </span>
          </div>
        ),
        width: 100,
        align: "left",
        ...getSortingFn("ID", sort, setSort, setFilters),
      },
      {
        id: "default-name",
        label: "Name",
        Header: "Name",
        accessor: (row) => (
          <div className="flex" style={{ overflow: "auto", width: "100%", height: "100%" }}>
            <MultiEditInputForm id={`name`} row={row} readOnly />
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getSortingFn("NAME", sort, setSort, setFilters),
      },
      {
        id: "default-sourcepath",
        label: "Source path",
        Header: "Source path",
        accessor: (row) => (
          // <div className={styles.container_ellipsis} style={{ padding: "7px" }}>
          //   <span>{row.path}</span>

          // </div>
          <div className="flex" style={{ overflow: "auto", width: "100%", height: "100%" }}>
            <MultiEditInputForm id={`path`} row={row} readOnly />
          </div>
        ),
        width: 200,
        align: "left",
      },
      {
        id: "default-projects",
        label: "Projects",
        Header: (
          <MultiEditHeaderWrapper<Dataset>
            control={control}
            getValues={getValues}
            ids={selection}
            label="Projects"
            property="projects"
            required={isRequired.current}
            hasApply
            hasAppend
          >
            {(consolidatedValues) => (
              <ProjectsVirtualizedSelectForm
                id="projects"
                control={control}
                hasLabel={false}
                placeholder={getValueOrDefault(consolidatedValues?.projects)}
                defaultValue={consolidatedValues?.projects?.value ?? null}
                horizontal
                allowCreateEntity
                allowUnassigned
                uncontained
                // showControls
                isMulti
                filters={{ currentUserHasAddPermission: true }}
              />
            )}
          </MultiEditHeaderWrapper>
        ),
        accessor: (row, rowIndex, columnIndex) => (
          <div className="flex" style={{ overflow: "auto", width: "100%", height: "100%" }}>
            <MultiEditSelectForm
              id={`projects`}
              entityConstants={projectsConstants}
              row={row}
              isMulti
              required={isRequired.current}
            />
          </div>
        ),
        minWidth: 400,
        width: 400,
      },
    ];
    res.push({
      id: "default-customType",
      label: "Custom Type",
      Header: (
        <MultiEditHeaderWrapper<Dataset>
          control={control}
          getValues={getValues}
          ids={selection}
          label="Dataset Type"
          additionalControls={
            <GenericBooleanStateProvider>
              {({ isTrue: showModal, setIsTrue: setShowModal }) => (
                <>
                  <AlertModal
                    type="success"
                    title="Confirm action"
                    description={`Switching between ${customTypeConstants.entityPlural} can lead to data loss. Please contact your system administrator if you are unsure about the implications.`}
                    showModal={showModal}
                    setShowModal={setShowModal}
                    proceedLabel={"Enable editing"}
                    // onCancel={() => setShowModal(false)}
                    onProceed={() => setTypeOverride(true)}
                    forceUserInput
                    forceUserInputText="EDIT"
                  />
                  {!typeOverride && (
                    <button className="btn btn-sm btn-success" onClick={() => setShowModal(true)}>
                      <LucideIcon name="square-pen" /> Enable editing
                    </button>
                  )}
                </>
              )}
            </GenericBooleanStateProvider>
          }
          property="customType"
          disabled={() => !typeOverride}
          // required
          hasApply
        >
          {(consolidatedValues) => (
            <CustomTypesVirtualizedSelectForm
              id="customType"
              control={control}
              hasLabel={false}
              placeholder={getValueOrDefault(consolidatedValues?.customType)}
              defaultValue={consolidatedValues?.customType?.value ?? null}
              disabled={!typeOverride}
              horizontal
              allowUnassigned
              uncontained
              filters={{ entityTypes: ["Dataset"] }}
              // showControls
            />
          )}
        </MultiEditHeaderWrapper>
      ),
      accessor: (row, rowIndex, columnIndex) => (
        <MultiEditSelectForm
          id={`customType`}
          entityConstants={customTypeConstants}
          row={row}
          required
          disabled={!typeOverride}
          filters={{ entityTypes: ["Dataset"] }}
        />
      ),
      minWidth: 300,
      width: 300,
      ...getSortingFn("CUSTOM_TYPE", sort, setSort, setFilters),
    });
    if (types) {
      res = res.concat(cfColumns);
    }
    return res;
  }, [cfColumns, control, getValues, selection, sort, typeOverride, types]);

  const columnWidths = useMemo(
    (): ColumnWidthSettings => Object.fromEntries(columns.map((col) => [col.id, col.width])),
    [columns]
  );
  const columnSettings = useMemo(
    (): ColumnsSettings<Dataset> =>
      Object.fromEntries(
        columns.map((col, idx) => [
          col.id,
          {
            pos: idx,
            active: true,
            header: col.label ?? col.id,
          },
        ])
      ),
    [columns]
  );

  const onSelectionChange = useCallback((selection: Set<number>) => {
    setSelection(selection);
  }, []);

  const onSuccessCallback = useCallback(
    (data: Dataset[]) => {
      const _data = Object.fromEntries(Object.entries(data).map(([, d]) => [d.id, d]));
      history.replace({ state: { ...location.state, items: _data } });
    },
    [history, location.state]
  );

  const beforeSubmit = useCallback(
    (data: MultiEditMappedItemObject<Dataset>, claimMode: "claim" | "unclaim") => {
      return Object.fromEntries(
        Object.entries(data).map(([id, d]) => [
          id,
          {
            ...d,
            claimed: claimMode === "claim" ? true : false,
            owner: session?.person ?? null,
            ...(d.customType && types && processValuesForTypedEntity(d, types?.[d.customType.id])),
          },
        ])
      );
    },
    [types, session?.person]
  );

  const beforeInit = useCallback(
    (data: MultiEditMappedItemObject<Dataset>) => {
      return Object.fromEntries(
        Object.entries(data).map(([id, d]) => [
          id,
          {
            ...d,
            customFields: {},
            ...(d.customType && types && processInitialValuesForTypedEntity(d, types?.[d.customType.id])),
          },
        ])
      );
    },
    [types]
  );

  const { onSubmit, loading } = useMultiEditUtils<Dataset>({
    entityConstants: datasetsConstants,
    showToast: true,
    onSuccessCallback: onSuccessCallback,
    beforeReset: beforeInit,
  });

  const { conditionalTypeFormSchema } = useCustomFieldsValidationObjectShape();

  const conditionalDatasetClaimFormSchema = useMemo(
    () => ({
      projects: yup
        .array()
        .of(yup.object().shape({ id: yup.number().required().positive().integer(), name: yup.string().required() }))
        .min(1, "At least one project is required")
        .typeError("At least one project is required"),
      operators: yup
        .array()
        .of(yup.object().shape({ id: yup.number().required().positive().integer(), name: yup.string().required() }))
        .min(1, "At least one operator is required")
        .typeError("At least one operator is required"),
      sample: yup
        .object()
        .shape({ id: yup.number().required().positive().integer(), name: yup.string().required() })
        .typeError("A sample must be defined"),
      ...conditionalTypeFormSchema,
    }),
    [conditionalTypeFormSchema]
  );

  const resolverCallback = useCallback(
    (schema: yup.ObjectSchema<any>) => {
      const resolverOverride = async (data: MultiEditMappedItemObject<Dataset>, context: any, options: any) => {
        if (isRequired.current) {
          const _schema = generateBulkEditYupSchema(data, conditionalDatasetClaimFormSchema);
          return await yupResolver(_schema)(data, context, options);
        } else {
          const _schema = generateBulkEditYupSchema(data, conditionalTypeFormSchema);
          return await yupResolver(_schema)(data, context, options);
          // return {
          //   values: data,
          //   errors: {},
          // };
        }
      };
      return resolverOverride;
    },
    [conditionalDatasetClaimFormSchema, conditionalTypeFormSchema]
  );

  if (initItems && !Object.keys(initItems).length && !Object.keys(defaultFilters).length)
    return <Alert type="info" message="Bulk editing is only available to a prior selection" fit centered />;
  return (
    <MultiEditProvider<Dataset>
      resource={datasetsConstants.resource}
      initItems={initItems}
      ids={defaultFilters.ids ?? []}
      entityValidationSchema={conditionalDatasetClaimFormSchema} // A MultiEditMapped version will be passed to the custom resolverCallback
      resolverCallback={resolverCallback} // We inject a custom resolver, as we have to validate conditionally (e.g. claim or edit)
      beforeInit={beforeInit}
      isCustomSchemaEntity
      canAbort
    >
      {({ consolidatedValues, items, consolidatedCustomFieldValues, customFieldPlaceholders }) => {
        return (
          <MultiEditTable<Dataset, DatasetFilters>
            entityConstants={datasetsConstants}
            columns={columns}
            filters={filters}
            defaultSelection={defaultFilters.ids ?? undefined}
            onSelectionChange={onSelectionChange}
            onSubmit={onSubmit}
            loading={loading}
            functionRef={functionRef}
            columnWidths={columnWidths}
            defaultColumnSettings={columnSettings}
            additionalSaveButtons={(handleSubmit, reset, isSubmitting, isDirty, nChanges) => (
              <>
                {/* {consolidatedValues.claimed?.isEqual && consolidatedValues.claimed?.value && (
                <button
                  className={`btn btn-soft-danger ${isSubmitting ? "loading" : ""}`}
                  title="Edit & unclaim"
                  onClick={async () => {
                    await handleSubmit(
                      async (data: any) =>
                        await onSubmit(
                          beforeSubmit(data, "unclaim"),
                          Object.keys(data).length,
                          reset,
                          "edited & unclaimed"
                        )
                    )();
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting && <span className="spinner" />} Edit & unclaim {`${isDirty ? `(${nChanges})` : ""}`}
                </button>
              )} */}
                {consolidatedValues.claimed?.isEqual && !consolidatedValues.claimed?.value && (
                  <Button
                    className={`btn btn-primary ${isSubmitting ? "loading" : ""}`}
                    title="Edit & claim"
                    onClick={async () => {
                      // We trigger validation for claiming
                      isRequired.current = true;
                      await handleSubmit(
                        async (data: any) =>
                          await onSubmit(
                            beforeSubmit(data, "claim"),
                            Object.keys(data).length,
                            reset,
                            "edited & claimed"
                          )
                      )();
                      isRequired.current = false;
                    }}
                    disabled={isSubmitting}
                    loading={loading}
                  >
                    Edit & claim {isDirty ? <span className="badge">{nChanges}</span> : ""}
                  </Button>
                )}
              </>
            )}
            fixedRowRendererColumnWidth={fixedRowRendererColumnWidth}
            fixedRowRenderer={fixedRowRenderer}
            fixedRowRendererColumn={fixedRowRendererColumn}
          />
        );
      }}
    </MultiEditProvider>
  );
};
